import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { commentsApi } from '../../api/commentsApi'
import type { Comment, CommentDto, CommentsState } from '../../Models/Task'

function mapDto(d: CommentDto): Comment {
  return {
    id: d.id,
    taskId: d.taskId,
    body: d.body,
    createdAt: d.createdAt,
  }
}

function errorMessage(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: string } | undefined
    return data?.error ?? e.message ?? fallback
  }
  if (e instanceof Error) return e.message
  if (
    typeof e === 'object' &&
    e !== null &&
    'message' in e &&
    typeof (e as { message: unknown }).message === 'string'
  ) {
    return (e as { message: string }).message
  }
  return fallback
}

const initialState: CommentsState = {
  byTaskId: {},
  status: 'idle',
  error: undefined,
}

export const fetchComments = createAsyncThunk<CommentDto[], string, { rejectValue: string }>(
  'comments/fetch',
  async (taskId, thunkAPI) => {
    try {
      return await commentsApi.getByTaskId(taskId)
    } catch (e) {
      console.error(e)
      return thunkAPI.rejectWithValue(errorMessage(e, 'Failed to load comments.'))
    }
  }
)

export const createComment = createAsyncThunk<
  CommentDto,
  { taskId: string; body: string },
  { rejectValue: string }
>('comments/create', async ({ taskId, body }, thunkAPI) => {
  try {
    return await commentsApi.create(taskId, body)
  } catch (e) {
    console.error(e)
    return thunkAPI.rejectWithValue(errorMessage(e, 'Failed to post comment.'))
  }
})

export const deleteComment = createAsyncThunk<
  { taskId: string; id: string },
  { taskId: string; id: string },
  { rejectValue: string }
>('comments/delete', async ({ taskId, id }, thunkAPI) => {
  try {
    await commentsApi.delete(taskId, id)
    return { taskId, id }
  } catch (e) {
    console.error(e)
    return thunkAPI.rejectWithValue(errorMessage(e, 'Failed to delete comment.'))
  }
})

const commentsSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    clearCommentsError: (state) => {
      state.error = undefined
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchComments.pending, (state) => {
        state.status = 'loading'
        state.error = undefined
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const taskId = action.meta.arg
        state.byTaskId[taskId] = action.payload.map(mapDto)
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload ?? 'Failed to load comments.'
      })
      .addCase(createComment.fulfilled, (state, action) => {
        const c = mapDto(action.payload)
        const list = state.byTaskId[c.taskId] ?? []
        state.byTaskId[c.taskId] = [...list, c].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )
      })
      .addCase(createComment.rejected, (state, action) => {
        state.error = action.payload ?? 'Failed to post comment.'
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { taskId, id } = action.payload
        const list = state.byTaskId[taskId]
        if (list) {
          state.byTaskId[taskId] = list.filter((c) => c.id !== id)
        }
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.error = action.payload ?? 'Failed to delete comment.'
      })
  },
})

export const { clearCommentsError } = commentsSlice.actions
export default commentsSlice.reducer
